import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, AlertCircle, RefreshCw, Server } from 'lucide-react';
import { log } from '../../lib/logger';

/**
 * Admin post-deploy health dashboard widget (iter104b).
 *
 * Calls `GET /api/health?probe_smtp=true` (admin-only) on mount + every
 * 60 s and shows a green/amber/red dot for each integration. Hover any
 * dot for the underlying status detail.
 *
 * Components shown:
 *   * Mongo            — DB ping + collection count snapshot
 *   * Scheduler        — APScheduler job list + next-run timestamps
 *   * SMTP             — Office365 STARTTLS + AUTH probe
 *   * Converge         — env-var presence (Account ID + User ID + PIN)
 *   * Freightcom       — env-var presence (API key)
 *
 * Why a widget vs a separate page?
 * One-glance "everything is up" check is most valuable IMMEDIATELY
 * after deploy, when the operator is already in /admin verifying
 * a customer's order shipped. A dedicated page would require an
 * extra navigation; the dots live here.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 60_000;

const STATUS_COLORS = {
  green: { bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-300', icon: CheckCircle2 },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', ring: 'ring-amber-300', icon: AlertTriangle },
  red: { bg: 'bg-red-100', text: 'text-red-700', ring: 'ring-red-300', icon: AlertCircle },
  gray: { bg: 'bg-gray-100', text: 'text-gray-500', ring: 'ring-gray-200', icon: AlertCircle },
};

const Dot = ({ label, color, detail, testId }) => {
  const cfg = STATUS_COLORS[color] || STATUS_COLORS.gray;
  const Icon = cfg.icon;
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${cfg.bg} ${cfg.text} ring-1 ${cfg.ring} text-xs font-medium`}
      title={detail}
      data-testid={testId}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
    </div>
  );
};

const SystemHealthWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastFetched, setLastFetched] = useState(null);

  const fetchHealth = useCallback(async ({ userTriggered = false } = {}) => {
    setError('');
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/health?probe_smtp=true`);
      setData(r.data);
      setLastFetched(new Date());
      // iter114s.90 — On manual clicks the probe usually completes in
      // ~200ms, faster than the spinner can finish a full revolution.
      // Without a toast operators were convinced the button "did
      // nothing". A short success toast + the updated "Checked" timestamp
      // together make the refresh action obviously effective.
      if (userTriggered) {
        toast.success('System health refreshed', {
          description: `${Object.keys(r.data || {}).length} subsystem(s) probed.`,
        });
      }
    } catch (err) {
      log.warn('Health probe failed', err);
      const detail = err.response?.data?.detail || 'Probe failed';
      setError(detail);
      if (userTriggered) {
        toast.error(`Health probe failed: ${detail}`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(() => fetchHealth(), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchHealth]);

  // Translate raw health response → 5 dot statuses + details.
  const dots = (() => {
    if (!data) return [];
    const out = [];
    // Mongo
    out.push({
      key: 'mongo',
      label: 'Mongo',
      color: data.mongo?.status === 'ok' ? 'green' : 'red',
      detail: data.mongo?.status === 'ok'
        ? `OK — users: ${data.mongo.users}, orders: ${data.mongo.enhanced_orders}`
        : `ERROR — ${data.mongo?.error || 'unknown'}`,
    });
    // Scheduler
    out.push({
      key: 'scheduler',
      label: 'Scheduler',
      color: data.scheduler?.running ? 'green' : (data.scheduler?.status === 'disabled' ? 'gray' : 'red'),
      detail: data.scheduler?.jobs?.length
        ? `Running — ${data.scheduler.jobs.length} jobs registered: ${data.scheduler.jobs.map((j) => j.id).join(', ')}`
        : (data.scheduler?.error || 'No jobs registered'),
    });
    // SMTP
    const smtp = data.smtp || {};
    let smtpColor = 'gray';
    if (smtp.status === 'ok') smtpColor = 'green';
    else if (smtp.status === 'configured') smtpColor = 'amber';
    else if (smtp.status === 'unconfigured') smtpColor = 'gray';
    else smtpColor = 'red';
    out.push({
      key: 'smtp',
      label: 'SMTP',
      color: smtpColor,
      detail: smtp.status === 'ok'
        ? `Office365 login OK (${smtp.host}:${smtp.port})`
        : `${smtp.status}${smtp.error ? ' — ' + smtp.error : ''}`,
    });
    // Converge
    const converge = data.integrations?.converge || {};
    out.push({
      key: 'converge',
      label: 'Converge',
      color: converge.configured ? 'green' : 'amber',
      detail: converge.configured
        ? `Configured (${converge.env || 'demo'} env)`
        : 'Missing CONVERGE_ACCOUNT_ID / USER_ID / PIN',
    });
    // Freightcom
    const fc = data.integrations?.freightcom || {};
    out.push({
      key: 'freightcom',
      label: 'Freightcom',
      color: fc.configured ? 'green' : 'amber',
      detail: fc.configured ? `Configured (${fc.base_url || 'unknown env'})` : 'Missing FREIGHTCOM_API_KEY',
    });
    return out;
  })();

  const overallColor = (() => {
    if (loading) return 'gray';
    if (error) return 'red';
    if (dots.some((d) => d.color === 'red')) return 'red';
    if (dots.some((d) => d.color === 'amber')) return 'amber';
    return 'green';
  })();

  return (
    <div
      className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
      data-testid="system-health-widget"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">System Health</h3>
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              overallColor === 'green' ? 'bg-emerald-500' :
              overallColor === 'amber' ? 'bg-amber-500' :
              overallColor === 'red' ? 'bg-red-500' : 'bg-gray-400'
            } animate-pulse`}
            data-testid="system-health-overall-dot"
            aria-label={`Overall: ${overallColor}`}
            title={`Overall: ${overallColor}`}
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {lastFetched && (
            <span data-testid="system-health-last-fetched">
              Checked {lastFetched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            type="button"
            onClick={() => fetchHealth({ userTriggered: true })}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 transition-colors"
            aria-label="Refresh health check"
            data-testid="system-health-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span className="font-medium">{loading ? 'Probing…' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 mb-2" data-testid="system-health-error">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {loading && !data && (
          <p className="text-sm text-gray-500 italic">Probing integrations…</p>
        )}
        {dots.map((d) => (
          <Dot
            key={d.key}
            label={d.label}
            color={d.color}
            detail={d.detail}
            testId={`system-health-dot-${d.key}`}
          />
        ))}
      </div>
    </div>
  );
};

export default SystemHealthWidget;
